import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { type Api, listSections } from "api-client";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "ui";

import { DataTable } from "src/components/DataTable";
import { type DataTableFeatures } from "src/components/dataTableFeatures";
import { useTableSearch } from "src/components/TableSearchContext";
import { SectionDetails } from "src/sections/SectionsTable";

type Section = Api.ListSections.Output.Self["currentPage"][number];

interface Props {
  onPreview: (sectionId: string) => void;
  onImport: (sectionId: string) => void;
  importingSectionId: string | null;
}

export function SectionSelectionTableContent(props: Props) {
  const translation = useTranslation();
  const tableSearch = useTableSearch();
  const queryParams = tableSearch.queryParams;
  const query = useQuery({
    queryKey: [
      "sections",
      queryParams.q,
      queryParams.pageIndex,
      queryParams.pageSize,
    ],
    queryFn: async () => {
      const res = await listSections({
        query: {
          query: queryParams.q || undefined,
          pageIndex: queryParams.pageIndex,
          pageSize: queryParams.pageSize,
        },
      });
      return res.data.data;
    },
    placeholderData: (previousData) => previousData,
  });
  const sections = query.data?.currentPage ?? [];
  const columns: ColumnDef<DataTableFeatures, Section>[] = [
    {
      id: "name",
      header: translation.t("sectionsPage.name"),
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block max-w-[220px] truncate font-medium">
              {row.original.name}
            </span>
          </TooltipTrigger>
          <TooltipContent>{row.original.name}</TooltipContent>
        </Tooltip>
      ),
    },
    {
      id: "details",
      header: translation.t("sectionsPage.details"),
      cell: ({ row }) => <SectionDetails section={row.original} />,
    },
    {
      id: "actions",
      header: () => null,
      size: 180,
      cell: ({ row }) => {
        const isImporting = props.importingSectionId === row.original.id;
        const isDisabled = props.importingSectionId !== null && !isImporting;
        return (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={isDisabled || isImporting}
              onClick={() => props.onPreview(row.original.id)}
            >
              {translation.t("editWorkout.importSection.preview")}
            </Button>
            <Button
              size="sm"
              disabled={isDisabled || isImporting}
              onClick={() => props.onImport(row.original.id)}
            >
              {isImporting && (
                <Icon name="tabler-loader-2" className="h-4 w-4 animate-spin" />
              )}
              {translation.t("editWorkout.importSection.import")}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={sections}
      getRowId={(row) => row.id}
      searchPlaceholder={translation.t(
        "editWorkout.importSection.searchPlaceholder",
      )}
      globalFilter={tableSearch.q}
      onGlobalFilterChange={tableSearch.setSearch}
      manualFiltering
      manualPagination
      rowCount={query.data?.pageInfo.count ?? 0}
      pagination={tableSearch.pagination}
      onPaginationChange={tableSearch.onPaginationChange}
      pageSizeOptions={[10]}
      hideRowsPerPage
      hidePagination={query.isLoading || sections.length === 0}
      isLoading={query.isLoading}
      emptyState={{
        icon: "tabler-section",
        title: translation.t("editWorkout.importSection.empty"),
      }}
    />
  );
}
